import React, { useEffect, useState } from "react";
import { api } from "../api";

const CATEGORY_LABEL = { ski: "Ski", boots: "Schuhe", poles: "Stöcke", binding: "Bindungen", lurk: "Lurk" };

function presetRange(preset) {
  const now = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (preset === "today") return { from: fmt(now), to: fmt(now) };
  if (preset === "7d") {
    const d = new Date(now); d.setDate(d.getDate() - 6);
    return { from: fmt(d), to: fmt(now) };
  }
  if (preset === "30d") {
    const d = new Date(now); d.setDate(d.getDate() - 29);
    return { from: fmt(d), to: fmt(now) };
  }
  if (preset === "season") {
    // Skisaison: November (Vorjahr) — April
    const year = now.getMonth() < 5 ? now.getFullYear() - 1 : now.getFullYear();
    return { from: `${year}-11-01`, to: `${year + 1}-04-30` };
  }
  return { from: "", to: "" };
}

export default function Recap() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (r = range) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.recap({ date_from: r.from, date_to: r.to });
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* initial: alle */ // eslint-disable-next-line
  }, []);

  const applyPreset = (p) => {
    const r = presetRange(p);
    setRange(r);
    load(r);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl">Event-Recap</h1>
        <p className="text-fh-gray text-sm mt-1">
          Statistik über einen Zeitraum — Kunden werden per Email-Adresse dedupliziert.
        </p>
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label-field">Von</label>
            <input
              type="date"
              className="input-field"
              value={range.from}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
            />
          </div>
          <div>
            <label className="label-field">Bis</label>
            <input
              type="date"
              className="input-field"
              value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
            />
          </div>
          <button onClick={() => load()} className="btn-primary" disabled={loading}>
            {loading ? "Lade…" : "Aktualisieren"}
          </button>
          <button onClick={() => applyPreset("clear")} className="btn-ghost">Alles ansehen</button>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {[
            { v: "today", l: "Heute" },
            { v: "7d", l: "Letzte 7 Tage" },
            { v: "30d", l: "Letzte 30 Tage" },
            { v: "season", l: "Saison" },
          ].map((p) => (
            <button
              key={p.v}
              onClick={() => applyPreset(p.v)}
              className="px-2.5 py-1 rounded bg-fh-gray-light hover:bg-fh-black hover:text-white transition-colors"
            >
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-fh-red/10 border-l-4 border-fh-red px-4 py-3 rounded text-sm text-fh-red-dark">{error}</div>
      )}

      {data && <RecapBody data={data} />}
    </div>
  );
}

function RecapBody({ data }) {
  const reg = data.registrations;
  const items = data.items;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Anmeldungen" value={reg.total_rentals} hint={`davon ${reg.unique_customers} unique Kunden`} />
        <Stat label="Items verliehen" value={items.total_assignments} hint={`${items.distinct_items_used} verschiedene Items`} accent="text-fh-red" />
        <Stat
          label="Aktiv / Zurück"
          value={`${reg.active} / ${reg.returned}`}
          hint={`Auslastung: ${items.total_in_inventory ? Math.round((items.distinct_items_used / items.total_in_inventory) * 100) : 0}%`}
        />
        <Stat
          label="Verleih-Dauer (Median)"
          value={data.duration?.median_hours != null ? `${data.duration.median_hours} h` : "—"}
          hint={data.duration?.n_returned ? `n=${data.duration.n_returned}` : ""}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <h3 className="font-semibold mb-3">📦 Pro Kategorie</h3>
          {items.by_category.length === 0 ? (
            <p className="text-sm text-fh-gray">Noch keine Verleihe.</p>
          ) : (
            <BarList
              rows={items.by_category.map((c) => ({ label: CATEGORY_LABEL[c.category] || c.category, value: c.count }))}
            />
          )}
        </div>

        <div className="card p-5">
          <h3 className="font-semibold mb-3">📍 Pro Standort</h3>
          {data.by_location.length === 0 ? (
            <p className="text-sm text-fh-gray">Keine Standort-Daten.</p>
          ) : (
            <BarList rows={data.by_location.map((l) => ({ label: l.name, value: l.rentals }))} />
          )}
        </div>

        <div className="card p-5">
          <h3 className="font-semibold mb-3">🔝 Top-Items</h3>
          {items.top_items.length === 0 ? (
            <p className="text-sm text-fh-gray">Noch keine Verleihe.</p>
          ) : (
            <ul className="text-sm divide-y divide-gray-100">
              {items.top_items.map((i) => (
                <li key={i.id} className="py-2 flex justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono">{i.id}</div>
                    <div className="text-xs text-fh-gray truncate">{i.label}</div>
                  </div>
                  <div className="font-semibold">{i.count}×</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-5">
          <h3 className="font-semibold mb-3">🏷️ Top-Modelle</h3>
          {items.top_models.length === 0 ? (
            <p className="text-sm text-fh-gray">Noch keine Verleihe.</p>
          ) : (
            <ul className="text-sm divide-y divide-gray-100">
              {items.top_models.map((m, idx) => (
                <li key={idx} className="py-2 flex justify-between gap-3">
                  <span className="truncate">{m.label}</span>
                  <span className="font-semibold">{m.count}×</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold">👥 Mitarbeiter</h3>
        </div>
        {data.staff.length === 0 ? (
          <div className="p-6 text-center text-fh-gray text-sm">Noch keine Aktivität.</div>
        ) : (
          <>
            <table className="w-full text-sm hidden md:table">
              <thead className="bg-fh-gray-light text-fh-gray text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Mitarbeiter</th>
                  <th className="text-right px-4 py-2">Verleihe erstellt</th>
                  <th className="text-right px-4 py-2">Items raus</th>
                  <th className="text-right px-4 py-2">Items zurück</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.staff.map((s) => (
                  <tr key={s.user_id} className="hover:bg-fh-gray-light">
                    <td className="px-4 py-2">
                      <div className="font-medium">{s.full_name || s.username}</div>
                      <div className="text-xs text-fh-gray">{s.user_id}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{s.rentals_created}</td>
                    <td className="px-4 py-2 text-right">{s.items_out}</td>
                    <td className="px-4 py-2 text-right">{s.items_back}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <ul className="divide-y divide-gray-100 md:hidden">
              {data.staff.map((s) => (
                <li key={s.user_id} className="px-4 py-3">
                  <div className="font-medium">{s.full_name || s.username}</div>
                  <div className="text-xs text-fh-gray mb-2">{s.user_id}</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <Mini label="Verleihe" value={s.rentals_created} />
                    <Mini label="Items raus" value={s.items_out} />
                    <Mini label="Items zurück" value={s.items_back} />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-fh-gray-light rounded p-2 text-center">
      <div className="text-fh-gray uppercase tracking-wider">{label}</div>
      <div className="font-semibold text-fh-black text-base">{value}</div>
    </div>
  );
}

function Stat({ label, value, hint, accent }) {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wider text-fh-gray font-semibold">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent || ""}`}>{value}</div>
      {hint && <div className="text-xs text-fh-gray mt-1">{hint}</div>}
    </div>
  );
}

function BarList({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((r, idx) => (
        <li key={idx}>
          <div className="flex justify-between mb-1">
            <span>{r.label}</span>
            <span className="font-semibold">{r.value}</span>
          </div>
          <div className="h-2 bg-fh-gray-light rounded overflow-hidden">
            <div
              className="h-full bg-fh-red"
              style={{ width: `${(r.value / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
